import { Duration } from "luxon";
import {
  GetVendorDto,
  PatchVendorDto,
  VendorListDto,
} from "mock-api/src/shared-v2-types";
import { getSiteRouteUrl, SiteModelReference } from "src/app/core/sites";
import { TimeOfDay } from "src/app/core/time-of-day.model";
import { Weekdays } from "src/app/core/weekdays.model";
import { getApiDetailsDecorator, parseDuration } from "src/utils";

const api = getApiDetailsDecorator<VendorListDto | PatchVendorDto>();

export abstract class BaseVendor {
  protected constructor(args: ClassProperties<BaseVendor>) {
    this.areUnreservedSlotsOffered = args.areUnreservedSlotsOffered;
    this.contactEmails = args.contactEmails;
    this.endTime = args.endTime;
    this.isSameDayAppointmentAllowed = args.isSameDayAppointmentAllowed;
    this.maxAppointmentDoorCount = args.maxAppointmentDoorCount;
    this.maximumLoadCount = args.maximumLoadCount;
    this.maximumUnloadDurationPerUnit = args.maximumUnloadDurationPerUnit;
    this.startTime = args.startTime;
    this.unloadDurationPerUnit = args.unloadDurationPerUnit;
    this.weekdays = args.weekdays;
  }

  @api({ key: "offerUnreservedSlots" })
  public readonly areUnreservedSlotsOffered: boolean;
  @api({ key: "appointmentNotificationEmails" }) public readonly contactEmails:
    string | null;
  @api() public readonly endTime: TimeOfDay | null;
  @api({ key: "allowSameDayAppointment" })
  public readonly isSameDayAppointmentAllowed: boolean;
  @api({ key: "doorCount" }) public readonly maxAppointmentDoorCount: number;
  @api({ key: "maxLoadCount" }) public readonly maximumLoadCount: number | null;
  @api({ key: "maxCalcMinutesPerUnit" })
  public readonly maximumUnloadDurationPerUnit: Duration | null;
  @api() public readonly startTime: TimeOfDay | null;
  @api({ key: "minutesPerUnit" })
  public readonly unloadDurationPerUnit: Duration | null;
  @api({ key: "daysOfWeek" }) public readonly weekdays: Weekdays;

  public static deserializeBase(
    data: VendorListDto | GetVendorDto,
  ): BaseVendor {
    return {
      areUnreservedSlotsOffered: data.offerUnreservedSlots,
      contactEmails: data.appointmentNotificationEmails,
      endTime: data.endTime ? TimeOfDay.deserialize(data.endTime) : null,
      isSameDayAppointmentAllowed: data.allowSameDayAppointment,
      maxAppointmentDoorCount: data.doorCount,
      maximumLoadCount: data.maxLoadCount,
      maximumUnloadDurationPerUnit: parseDuration(
        data.maxCalcMinutesPerUnit,
        "minutes",
      ),
      startTime: data.startTime ? TimeOfDay.deserialize(data.startTime) : null,
      unloadDurationPerUnit: parseDuration(data.minutesPerUnit, "minutes"),
      weekdays: Weekdays.deserialize(data.daysOfWeek),
    };
  }
}

export function getVendorRouteUrl(
  vendor: VendorReference,
  ...childPaths: string[]
): string {
  return getSiteRouteUrl(
    vendor.site,
    "vendors",
    String(vendor.id),
    ...childPaths,
  );
}

export interface VendorReference extends SiteModelReference<"vendor"> {}
